import { useEffect, useMemo, useState } from "react";
import { Check, X, Video, Clock, CalendarPlus, Pencil, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { SectionTitle, Card } from "../ui";
import { meridaToDate } from "@/lib/tz";

const FIELD = "w-full bg-white border border-sand-200 rounded-lg px-3 py-2 text-sm text-charcoal-800 focus:outline-none focus:ring-2 focus:ring-sage/50";

const STATUS_STYLE = {
  requested: "bg-sand-100 text-charcoal-600",
  pending_payment: "bg-amber-100 text-amber-800",
  awaiting_approval: "bg-clay-100 text-clay-800",
  confirmed: "bg-sage-light text-sage-700",
  completed: "bg-emerald-100 text-emerald-700",
  no_show: "bg-purple-100 text-purple-700",
  declined: "bg-red-100 text-red-700",
  cancelled: "bg-red-100 text-red-700",
  expired: "bg-sand-100 text-charcoal-400",
};
const METHOD_LABELS = {
  video: "Video (in-site)", phone: "Phone", zoom: "Zoom", google_meet: "Google Meet", teams: "Teams",
  whatsapp_call: "WhatsApp call", whatsapp_video: "WhatsApp video", facetime: "FaceTime",
  in_person: "In person", invitee_location: "Client chooses place", custom: "Custom link",
};
const CAL_ACTIVE = ["requested", "pending_payment", "awaiting_approval", "confirmed", "completed", "no_show"];

function fmt(iso) {
  if (!iso) return "";
  const [d, t] = iso.slice(0, 16).split("T");
  const [y, mo, day] = d.split("-").map(Number);
  const [h, m] = (t || "00:00").split(":").map(Number);
  const date = new Date(y, mo - 1, day);
  const ampm = h >= 12 ? "PM" : "AM";
  return `${date.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}, ${h % 12 || 12}:${String(m).padStart(2, "0")} ${ampm}`;
}
function fmtLocal(iso) {
  const d = meridaToDate(iso);
  return d ? d.toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "";
}
const dkey = (d) => d.toISOString().slice(0, 10);
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };

function EditModal({ appt, employees, onClose, onSaved }) {
  const [f, setF] = useState({
    slot: appt.slot || "", duration_minutes: appt.duration_minutes || 45,
    employee_id: appt.employee_id || "", meeting_method: appt.meeting_method || "video",
    internal_notes: appt.internal_notes || "", status: appt.status,
  });
  const save = async () => {
    try {
      await api.patch(`/admin/appointments/${appt.id}`, f);
      toast.success("Appointment updated");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-charcoal-900/40 p-4" onClick={onClose}>
      <div data-testid="appt-edit-modal" className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-serif text-xl text-charcoal-800">Edit appointment</h3>
        <p className="mt-1 text-sm text-charcoal-500">{appt.name} · {appt.type_name} · {appt.email}</p>
        {appt.pricing_selection?.package_name && (
          <div data-testid="appt-package-badge" className="mt-3 rounded-lg bg-terracotta/10 border border-terracotta/30 px-3 py-2">
            <p className="text-xs font-medium text-terracotta">
              Pricing page selection: {appt.pricing_selection.package_name}
              {appt.pricing_selection.direction && <span className="ml-2 rounded-full bg-white px-2 py-0.5 text-charcoal-700">{appt.pricing_selection.direction}</span>}
              {appt.pricing_selection.estimate && <> · est. {appt.pricing_selection.estimate}</>}
            </p>
            {appt.pricing_selection.addons?.length > 0 && (
              <p className="mt-0.5 text-xs text-charcoal-600">Add-ons: {appt.pricing_selection.addons.join(", ")}</p>
            )}
          </div>
        )}
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <label className="text-sm text-charcoal-600">Date &amp; time
            <input data-testid="appt-edit-slot" type="datetime-local" className={FIELD} value={f.slot} onChange={(e) => setF({ ...f, slot: e.target.value })} /></label>
          <label className="text-sm text-charcoal-600">Duration (min)
            <input data-testid="appt-edit-duration" type="number" className={FIELD} value={f.duration_minutes} onChange={(e) => setF({ ...f, duration_minutes: Number(e.target.value) })} /></label>
          <label className="text-sm text-charcoal-600">Meeting method
            <select data-testid="appt-edit-method" className={FIELD} value={f.meeting_method} onChange={(e) => setF({ ...f, meeting_method: e.target.value })}>
              {Object.entries(METHOD_LABELS).map(([m, l]) => <option key={m} value={m}>{l}</option>)}
            </select></label>
          <label className="text-sm text-charcoal-600">Assigned employee
            <select data-testid="appt-edit-employee" className={FIELD} value={f.employee_id} onChange={(e) => setF({ ...f, employee_id: e.target.value })}>
              <option value="">Unassigned</option>
              {employees.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select></label>
          <label className="text-sm text-charcoal-600 sm:col-span-2">Status
            <select data-testid="appt-edit-status" className={FIELD} value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}>
              {["awaiting_approval", "confirmed", "completed", "cancelled", "no_show"].map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
            </select></label>
          <label className="text-sm text-charcoal-600 sm:col-span-2">Internal notes (never shown to client)
            <textarea data-testid="appt-edit-notes" rows={3} className={FIELD} value={f.internal_notes} onChange={(e) => setF({ ...f, internal_notes: e.target.value })} /></label>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-full border border-sand-200 px-5 py-2 text-sm text-charcoal-600">Cancel</button>
          <button data-testid="appt-edit-save" onClick={save} className="rounded-full bg-sage px-5 py-2 text-sm text-white font-medium hover:bg-sage-hover">Save changes</button>
        </div>
      </div>
    </div>
  );
}

function ApptChip({ a, onEdit }) {
  return (
    <button data-testid={`cal-appt-${a.id}`} onClick={() => onEdit(a)}
      className={`block w-full truncate rounded px-1.5 py-0.5 text-left text-[11px] ${STATUS_STYLE[a.status] || "bg-sand-100"}`}>
      {(a.slot || "").slice(11, 16)} {a.name}
    </button>
  );
}

function MonthView({ cursor, byDay, onEdit }) {
  const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const start = addDays(first, -((first.getDay() + 6) % 7));
  const weeks = Array.from({ length: 6 }, (_, w) => Array.from({ length: 7 }, (_, d) => addDays(start, w * 7 + d)));
  return (
    <div data-testid="cal-month" className="overflow-x-auto">
      <div className="grid min-w-[720px] grid-cols-7 gap-px rounded-xl border border-sand-200 bg-sand-200 overflow-hidden">
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => <div key={d} className="bg-sand-100 px-2 py-1.5 text-xs font-medium text-charcoal-600">{d}</div>)}
        {weeks.flat().map((d) => {
          const inMonth = d.getMonth() === cursor.getMonth();
          const appts = byDay[dkey(d)] || [];
          return (
            <div key={d.toISOString()} className={`min-h-[84px] space-y-0.5 p-1.5 ${inMonth ? "bg-white" : "bg-sand-50"}`}>
              <p className={`text-xs ${inMonth ? "text-charcoal-700" : "text-charcoal-300"}`}>{d.getDate()}</p>
              {appts.slice(0, 3).map((a) => <ApptChip key={a.id} a={a} onEdit={onEdit} />)}
              {appts.length > 3 && <p className="text-[10px] text-charcoal-400">+{appts.length - 3} more</p>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function WeekView({ cursor, byDay, onSelect, onDrop }) {
  const monday = addDays(cursor, -((cursor.getDay() + 6) % 7));
  const days = Array.from({ length: 7 }, (_, i) => addDays(monday, i));
  return <TimeGrid days={days} byDay={byDay} onSelect={onSelect} onDrop={onDrop} testid="cal-week" />;
}

function DayView({ cursor, byDay, onSelect, onDrop }) {
  return <TimeGrid days={[cursor]} byDay={byDay} onSelect={onSelect} onDrop={onDrop} testid="cal-day" />;
}

const HOUR_START = 7;
const HOUR_END = 21;
const HOUR_PX = 56;

const slotMinutes = (slot) => {
  const [h, m] = (slot || "").slice(11, 16).split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
};

function layoutLanes(appts) {
  const sorted = [...appts].sort((a, b) => slotMinutes(a.slot) - slotMinutes(b.slot));
  const laneEnds = [];
  const placed = sorted.map((a) => {
    const start = slotMinutes(a.slot);
    const end = start + Number(a.duration_minutes || 45);
    let lane = laneEnds.findIndex((laneEnd) => laneEnd <= start);
    if (lane === -1) { laneEnds.push(end); lane = laneEnds.length - 1; }
    else laneEnds[lane] = end;
    return { appt: a, lane };
  });
  return { placed, laneCount: Math.max(laneEnds.length, 1) };
}

function TimeGrid({ days, byDay, onSelect, onDrop, testid }) {
  const [dragOver, setDragOver] = useState(null);
  const hours = Array.from({ length: HOUR_END - HOUR_START }, (_, i) => HOUR_START + i);
  const totalH = (HOUR_END - HOUR_START) * HOUR_PX;
  const today = dkey(new Date());
  return (
    <div data-testid={testid} className="overflow-x-auto rounded-xl border border-sand-200 bg-white">
      <div className="grid min-w-[760px]" style={{ gridTemplateColumns: `56px repeat(${days.length}, 1fr)` }}>
        <div className="border-b border-sand-200 bg-sand-50" />
        {days.map((d) => (
          <div key={dkey(d)} className={`border-b border-l border-sand-200 px-2 py-2 text-center ${dkey(d) === today ? "bg-terracotta/5" : "bg-sand-50"}`}>
            <p className="text-xs font-medium text-charcoal-700">{d.toLocaleDateString("en-US", { weekday: "short" })}</p>
            <p className={`text-lg font-serif ${dkey(d) === today ? "text-terracotta" : "text-charcoal-800"}`}>{d.getDate()}</p>
          </div>
        ))}
        <div className="relative" style={{ height: totalH }}>
          {hours.map((h) => (
            <span key={h} className="absolute right-1.5 -translate-y-1/2 text-[10px] text-charcoal-400" style={{ top: (h - HOUR_START) * HOUR_PX }}>
              {h % 12 || 12}{h >= 12 ? "pm" : "am"}
            </span>
          ))}
        </div>
        {days.map((d) => {
          const dayIso = dkey(d);
          const appts = byDay[dayIso] || [];
          return (
            <div key={dayIso} className="relative border-l border-sand-200" style={{ height: totalH }}>
              {hours.map((h) => (
                <div key={h} className="absolute inset-x-0 border-t border-sand-100" style={{ top: (h - HOUR_START) * HOUR_PX }} />
              ))}
              {hours.flatMap((h) => [0, 30].map((mm) => {
                const mins = h * 60 + mm;
                const key = `${dayIso}T${String(h).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
                return (
                  <div key={key} data-testid={`drop-${key}`}
                    className={`absolute inset-x-0 ${dragOver === key ? "bg-sage/20 outline outline-1 outline-sage" : ""}`}
                    style={{ top: ((mins - HOUR_START * 60) / 60) * HOUR_PX, height: HOUR_PX / 2 }}
                    onDragOver={(e) => { e.preventDefault(); setDragOver(key); }}
                    onDragLeave={() => setDragOver((k) => (k === key ? null : k))}
                    onDrop={(e) => {
                      e.preventDefault();
                      setDragOver(null);
                      const id = e.dataTransfer.getData("text/plain");
                      if (id) onDrop(id, key);
                    }} />
                );
              }))}
              {layoutLanes(appts).placed.map(({ appt: a, lane }, _, arr) => {
                const laneCount = Math.max(...arr.map((p) => p.lane)) + 1;
                const start = slotMinutes(a.slot);
                const dur = Number(a.duration_minutes || 45);
                const top = ((start - HOUR_START * 60) / 60) * HOUR_PX;
                const height = Math.max((dur / 60) * HOUR_PX, 22);
                const width = 100 / laneCount;
                return (
                  <button key={a.id} data-testid={`cal-appt-${a.id}`} draggable
                    onDragStart={(e) => e.dataTransfer.setData("text/plain", a.id)}
                    onClick={() => onSelect(a)}
                    className={`absolute z-10 cursor-grab overflow-hidden rounded-md border border-white/70 px-1.5 py-0.5 text-left text-[11px] leading-tight shadow-sm hover:z-20 active:cursor-grabbing ${STATUS_STYLE[a.status] || "bg-sand-100"}`}
                    style={{ top: Math.max(top, 0), height, left: `calc(${lane * width}% + 2px)`, width: `calc(${width}% - 4px)` }}>
                    <span className="font-medium">{(a.slot || "").slice(11, 16)}</span> {a.name}
                    {height > 34 && <span className="block truncate opacity-70">{a.type_name}</span>}
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DriverAssign({ appt }) {
  const [drivers, setDrivers] = useState([]);
  const [current, setCurrent] = useState(appt.driver_id || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/drivers").then((r) => setDrivers(r.data || [])).catch(() => {}); }, []);
  useEffect(() => setCurrent(appt.driver_id || ""), [appt]);
  const assign = async (driverId) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/appointments/${appt.id}/assign-driver`, { driver_id: driverId });
      setCurrent(driverId);
      toast.success(driverId ? `${data.driver_name} assigned — intro email sent to the client` : "Driver unassigned");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not assign driver"); }
    setBusy(false);
  };
  return (
    <label className="block text-sm text-charcoal-600">Assigned driver (client gets an intro email)
      <select data-testid="panel-driver" className={FIELD} value={current} disabled={busy}
        onChange={(e) => assign(e.target.value)}>
        <option value="">No driver assigned</option>
        {drivers.map((d) => <option key={d.id} value={d.id}>{d.name}{d.active ? "" : " (inactive)"}</option>)}
      </select>
    </label>
  );
}

function SidePanel({ appt, employees, onClose, onAction, onSetStatus, onReassign, onEdit, saving }) {
  const local = meridaToDate(appt.slot);
  return (
    <div data-testid="appt-side-panel" className="fixed inset-y-0 right-0 z-50 flex w-full max-w-sm flex-col border-l border-sand-200 bg-white shadow-2xl">
      <div className="flex items-start justify-between border-b border-sand-100 p-5">
        <div>
          <p className="font-serif text-lg text-charcoal-800">{appt.name}</p>
          <p className="text-sm text-charcoal-500">{appt.type_name || "Consultation"} · {appt.duration_minutes}min</p>
        </div>
        <button data-testid="panel-close" onClick={onClose} aria-label="Close" className="text-charcoal-400 hover:text-charcoal-700"><X size={18} /></button>
      </div>
      <div className="flex-1 space-y-4 overflow-y-auto p-5">
        <div>
          <p data-testid="panel-time-merida" className="font-medium text-charcoal-800">{fmt(appt.slot)} <span className="text-xs font-normal text-charcoal-500">Mérida time</span></p>
          {local && <p data-testid="panel-time-local" className="text-xs text-charcoal-500">{local.toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })} your time</p>}
          <span className={`mt-2 inline-block rounded-full px-2.5 py-0.5 text-xs capitalize ${STATUS_STYLE[appt.status] || ""}`}>{appt.status.replace("_", " ")}</span>
        </div>
        <div className="space-y-1 text-sm text-charcoal-600">
          <p>{appt.email}</p>
          {appt.phone && <p>{appt.phone}</p>}
          <p>{METHOD_LABELS[appt.meeting_method] || appt.platform}</p>
          {appt.deposit_amount > 0 && <p>${appt.deposit_amount} deposit · {appt.deposit_paid ? "paid" : "unpaid"}</p>}
          {appt.notes && <p className="italic text-charcoal-500">"{appt.notes}"</p>}
          {(appt.preferred_name || appt.pets?.length > 0 || appt.personalization_notes) && (
            <div data-testid="panel-family-pets" className="mt-2 rounded-lg bg-sand-100 px-3 py-2 text-xs text-charcoal-700 space-y-1">
              {appt.preferred_name && <p>Call them: <span className="font-medium">{appt.preferred_name}</span></p>}
              {(appt.party_adults != null || appt.party_kids != null) && <p>Traveling: {appt.party_adults ?? 0} adult(s), {appt.party_kids ?? 0} kid(s)</p>}
              {appt.personalization_notes && <p>Comfort notes: "{appt.personalization_notes}"</p>}
              {(appt.pets || []).map((p, i) => (
                <p key={i}>🐾 <span className="font-medium">{p.name || "Pet"}</span>{p.species && ` (${p.species}${p.breed ? `, ${p.breed}` : ""})`}
                  {p.temperament && ` — ${p.temperament}`}{p.medical_needs && ` · medical: ${p.medical_needs}`}</p>
              ))}
            </div>
          )}
          {appt.meeting_link && <a href={appt.meeting_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-terracotta underline"><Video size={13} /> Meeting link</a>}
        </div>
        <label className="block text-sm text-charcoal-600">Assigned staff
          <select data-testid="panel-employee" className={FIELD} value={appt.employee_id || ""} disabled={saving}
            onChange={(e) => onReassign(appt.id, e.target.value)}>
            <option value="">Unassigned</option>
            {employees.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </label>
        <DriverAssign appt={appt} />
        <div className="grid grid-cols-2 gap-2 pt-1">
          {appt.status === "awaiting_approval" && (
            <button data-testid="panel-approve" disabled={saving} onClick={() => onAction(appt.id, "approve")} className="col-span-2 rounded-full bg-sage px-4 py-2 text-sm font-medium text-white hover:bg-sage-hover disabled:opacity-60">Approve &amp; confirm</button>
          )}
          {["requested", "awaiting_approval", "pending_payment"].includes(appt.status) && (
            <button data-testid="panel-decline" disabled={saving} onClick={() => onAction(appt.id, "decline")} className="rounded-full border border-sand-200 px-4 py-2 text-sm text-charcoal-600 hover:text-destructive disabled:opacity-60">Decline</button>
          )}
          {appt.status === "confirmed" && (
            <>
              <button data-testid="panel-complete" disabled={saving} onClick={() => onSetStatus(appt.id, "completed")} className="rounded-full border border-emerald-300 px-4 py-2 text-sm text-emerald-700 hover:bg-emerald-50 disabled:opacity-60">Completed</button>
              <button data-testid="panel-noshow" disabled={saving} onClick={() => onSetStatus(appt.id, "no_show")} className="rounded-full border border-purple-300 px-4 py-2 text-sm text-purple-700 hover:bg-purple-50 disabled:opacity-60">No-show</button>
              <button data-testid="panel-cancel" disabled={saving} onClick={() => onAction(appt.id, "cancel")} className="rounded-full border border-sand-200 px-4 py-2 text-sm text-charcoal-600 hover:text-destructive disabled:opacity-60">Cancel</button>
            </>
          )}
          <button data-testid="panel-edit" onClick={() => onEdit(appt)} className="rounded-full border border-sand-200 px-4 py-2 text-sm text-charcoal-700 hover:border-sage">Edit / reschedule</button>
        </div>
        <p className="text-xs text-charcoal-400">Tip: drag the appointment block on the calendar to reschedule it instantly.</p>
      </div>
    </div>
  );
}

export default function AppointmentsManager() {
  const [rows, setRows] = useState([]);
  const [types, setTypes] = useState([]);
  const [staff, setStaff] = useState([]);
  const [view, setView] = useState("week");
  const [cursor, setCursor] = useState(new Date());
  const [editing, setEditing] = useState(null);
  const [panel, setPanel] = useState(null);
  const [saving, setSaving] = useState(false);
  const [manual, setManual] = useState({ appointment_type_id: "", name: "", email: "", phone: "", slot: "", meeting_method: "video", employee_id: "", preferred_move_date: "", notes: "" });
  const [showManual, setShowManual] = useState(false);

  const load = () => api.get("/admin/appointments").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => {
    load();
    api.get("/admin/appointment-types").then((r) => setTypes(r.data)).catch(() => setTypes([]));
    api.get("/admin/employees").then((r) => setStaff(r.data || [])).catch(() => setStaff([]));
  }, []);

  const byDay = useMemo(() => {
    const m = {};
    rows.filter((a) => CAL_ACTIVE.includes(a.status)).forEach((a) => {
      const d = (a.slot || "").slice(0, 10);
      (m[d] = m[d] || []).push(a);
    });
    Object.values(m).forEach((l) => l.sort((x, y) => (x.slot > y.slot ? 1 : -1)));
    return m;
  }, [rows]);

  const act = async (id, action) => {
    setSaving(true);
    try { const { data } = await api.post(`/admin/appointments/${id}/${action}`); toast.success(`Appointment ${data.status || action}`); await load(); setPanel(null); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    setSaving(false);
  };
  const setStatus = async (id, status) => {
    setSaving(true);
    try { await api.patch(`/admin/appointments/${id}`, { status }); toast.success(`Marked ${status.replace("_", " ")}`); await load(); setPanel(null); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setSaving(false);
  };
  const reassign = async (id, employee_id) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/appointments/${id}`, { employee_id });
      toast.success("Staff updated");
      setPanel(data);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not reassign"); }
    setSaving(false);
  };
  const dropReschedule = async (id, newSlot) => {
    const appt = rows.find((r) => r.id === id);
    if (!appt || appt.slot === newSlot) return;
    const dur = Number(appt.duration_minutes || 45);
    const startA = new Date(newSlot).getTime();
    const endA = startA + dur * 60000;
    const conflict = rows.some((b) => {
      if (b.id === id || !CAL_ACTIVE.includes(b.status) || !b.slot_held) return false;
      const sameResource = !appt.employee_id || !b.employee_id || appt.employee_id === b.employee_id;
      if (!sameResource) return false;
      const startB = new Date(b.slot).getTime();
      const endB = startB + Number(b.duration_minutes || 45) * 60000;
      return startA < endB && endA > startB;
    });
    if (conflict) {
      toast.error("That would double-book a staff member — pick a different time.");
      return;
    }
    try {
      await api.patch(`/admin/appointments/${id}`, { slot: newSlot });
      toast.success(`Rescheduled to ${fmt(newSlot)} (Mérida)`);
      await load();
      setPanel((p) => (p && p.id === id ? { ...p, slot: newSlot } : p));
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not reschedule"); }
  };
  const createManual = async () => {
    if (!manual.appointment_type_id || !manual.name || !manual.email || !manual.slot) return toast.error("Type, name, email and slot are required");
    try { await api.post("/admin/appointments", manual); toast.success("Appointment created & confirmed"); setShowManual(false); setManual({ appointment_type_id: "", name: "", email: "", phone: "", slot: "", meeting_method: "video", employee_id: "", preferred_move_date: "", notes: "" }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not create"); }
  };
  const nav = (n) => setCursor((c) => (view === "month" ? new Date(c.getFullYear(), c.getMonth() + n, 1) : addDays(c, n * (view === "week" ? 7 : 1))));
  const pastConfirmed = (a) => a.status === "confirmed" && (a.slot || "") < new Date().toISOString().slice(0, 16);

  return (
    <div data-testid="admin-appointments">
      <SectionTitle title="Appointments" subtitle="Approve, edit, assign and track every booking — deposits are always collected before confirmation." />

      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex rounded-full border border-sand-200 bg-white p-1">
          {["list", "day", "week", "month"].map((v) => (
            <button key={v} data-testid={`appt-view-${v}`} onClick={() => setView(v)}
              className={`rounded-full px-4 py-1.5 text-sm capitalize transition-colors ${view === v ? "bg-charcoal-800 text-white" : "text-charcoal-600 hover:bg-sand-100"}`}>{v}</button>
          ))}
        </div>
        {view !== "list" && (
          <div className="flex items-center gap-2">
            <button data-testid="cal-prev" onClick={() => nav(-1)} className="rounded-full border border-sand-200 p-2 hover:bg-sand-100"><ChevronLeft size={16} /></button>
            <span className="min-w-[160px] text-center text-sm font-medium text-charcoal-700">
              {view === "month" ? cursor.toLocaleDateString("en-US", { month: "long", year: "numeric" }) : cursor.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
            </span>
            <button data-testid="cal-next" onClick={() => nav(1)} className="rounded-full border border-sand-200 p-2 hover:bg-sand-100"><ChevronRight size={16} /></button>
            <button onClick={() => setCursor(new Date())} className="rounded-full border border-sand-200 px-3 py-1.5 text-xs text-charcoal-600 hover:bg-sand-100">Today</button>
          </div>
        )}
        <button data-testid="appt-manual-toggle" onClick={() => setShowManual((s) => !s)} className="inline-flex items-center gap-2 rounded-full bg-sage px-5 py-2 text-sm text-white font-medium hover:bg-sage-hover">
          <CalendarPlus size={16} /> {showManual ? "Close" : "New appointment"}
        </button>
      </div>

      {showManual && (
        <Card className="mb-6">
          <div className="grid gap-3 sm:grid-cols-2">
            <select data-testid="appt-manual-type" className={FIELD} value={manual.appointment_type_id} onChange={(e) => setManual({ ...manual, appointment_type_id: e.target.value })}>
              <option value="">Select type…</option>
              {types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <input data-testid="appt-manual-slot" type="datetime-local" className={FIELD} value={manual.slot} onChange={(e) => setManual({ ...manual, slot: e.target.value })} />
            <input data-testid="appt-manual-name" className={FIELD} placeholder="Client name" value={manual.name} onChange={(e) => setManual({ ...manual, name: e.target.value })} />
            <input data-testid="appt-manual-email" className={FIELD} placeholder="Client email" value={manual.email} onChange={(e) => setManual({ ...manual, email: e.target.value })} />
            <select data-testid="appt-manual-method" className={FIELD} value={manual.meeting_method} onChange={(e) => setManual({ ...manual, meeting_method: e.target.value })}>
              {Object.entries(METHOD_LABELS).map(([m, l]) => <option key={m} value={m}>{l}</option>)}
            </select>
            <select data-testid="appt-manual-employee" className={FIELD} value={manual.employee_id} onChange={(e) => setManual({ ...manual, employee_id: e.target.value })}>
              <option value="">Any employee</option>
              {staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <button data-testid="appt-manual-create" onClick={createManual} className="mt-4 rounded-full bg-terracotta px-5 py-2 text-sm text-white font-medium hover:bg-terracotta-hover">Create &amp; confirm</button>
        </Card>
      )}

      {view === "month" && <MonthView cursor={cursor} byDay={byDay} onEdit={setPanel} />}
      {view === "week" && <WeekView cursor={cursor} byDay={byDay} onSelect={setPanel} onDrop={dropReschedule} />}
      {view === "day" && <DayView cursor={cursor} byDay={byDay} onSelect={setPanel} onDrop={dropReschedule} />}
      {view !== "list" && (
        <p data-testid="admin-tz-note" className="mt-3 text-xs text-charcoal-400">
          Calendar times are <b>Mérida, Mexico time</b> (business timezone). Click an appointment to see the equivalent in your local time. Drag any block to reschedule.
        </p>
      )}

      {view === "list" && (
        <div className="space-y-4">
          {rows.length === 0 && <p className="text-charcoal-500">No appointments yet.</p>}
          {rows.map((a) => (
            <Card key={a.id} data-testid={`appt-row-${a.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-medium text-charcoal-800">{a.name}</p>
                    <span className={`rounded-full px-2 py-0.5 text-xs ${STATUS_STYLE[a.status] || "bg-sand-100 text-charcoal-600"}`}>{a.status.replace("_", " ")}</span>
                    {a.deposit_amount > 0 && <span className="rounded-full bg-sand-100 px-2 py-0.5 text-xs text-charcoal-600">${a.deposit_amount} {a.deposit_paid ? "paid" : "unpaid"}</span>}
                    {(a.employee_name || a.assigned_to) && <span className="rounded-full bg-sage-light px-2 py-0.5 text-xs text-sage-700">{a.employee_name || a.assigned_to}</span>}
                  </div>
                  <p className="mt-1 text-sm text-charcoal-500">{a.email} · {a.phone || "no phone"}</p>
                  <p className="mt-1 text-sm text-charcoal-700">{a.type_name || "Consultation"} · {METHOD_LABELS[a.meeting_method] || a.platform}</p>
                  <p className="text-sm text-charcoal-600 inline-flex items-center gap-1.5"><Clock size={13} /> {fmt(a.slot)} <span className="text-xs text-charcoal-400">Mérida</span> · <span className="text-xs text-charcoal-400">{fmtLocal(a.slot)} your time</span> · {a.duration_minutes}min</p>
                  {a.preferred_move_date && <p className="text-xs text-charcoal-500 mt-1">Preferred move: {a.preferred_move_date}</p>}
                  {Object.keys(a.intake_answers || {}).length > 0 && (
                    <p className="text-xs text-charcoal-500 mt-1">{Object.entries(a.intake_answers).map(([q, v]) => `${q}: ${v}`).join(" · ")}</p>
                  )}
                  {a.notes && <p className="text-xs text-charcoal-500 mt-1 italic">"{a.notes}"</p>}
                  {a.internal_notes && <p className="text-xs text-clay-800 mt-1">🔒 {a.internal_notes}</p>}
                  {a.meeting_link && <a href={a.meeting_link} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1.5 text-sm text-terracotta underline"><Video size={13} /> Meeting link</a>}
                </div>
                <div className="flex shrink-0 flex-col gap-2">
                  <button data-testid={`appt-edit-${a.id}`} onClick={() => setEditing(a)} className="inline-flex items-center gap-1.5 rounded-full border border-sand-200 px-4 py-1.5 text-sm text-charcoal-600 hover:border-sage"><Pencil size={13} /> Edit</button>
                  {a.status === "awaiting_approval" && (
                    <button data-testid={`appt-approve-${a.id}`} onClick={() => act(a.id, "approve")} className="inline-flex items-center gap-1.5 rounded-full bg-sage px-4 py-1.5 text-sm text-white hover:bg-sage-hover"><Check size={14} /> Approve</button>
                  )}
                  {["requested", "awaiting_approval", "pending_payment"].includes(a.status) && (
                    <button data-testid={`appt-decline-${a.id}`} onClick={() => act(a.id, "decline")} className="inline-flex items-center gap-1.5 rounded-full border border-sand-200 px-4 py-1.5 text-sm text-charcoal-600 hover:text-destructive"><X size={14} /> Decline</button>
                  )}
                  {a.status === "confirmed" && (
                    <button data-testid={`appt-cancel-${a.id}`} onClick={() => act(a.id, "cancel")} className="rounded-full border border-sand-200 px-4 py-1.5 text-sm text-charcoal-600 hover:text-destructive">Cancel</button>
                  )}
                  {pastConfirmed(a) && (
                    <>
                      <button data-testid={`appt-complete-${a.id}`} onClick={() => setStatus(a.id, "completed")} className="rounded-full border border-sand-200 px-4 py-1.5 text-sm text-emerald-700 hover:border-emerald-400">Completed</button>
                      <button data-testid={`appt-noshow-${a.id}`} onClick={() => setStatus(a.id, "no_show")} className="rounded-full border border-sand-200 px-4 py-1.5 text-sm text-purple-700 hover:border-purple-400">No-show</button>
                    </>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {panel && (
        <SidePanel appt={panel} employees={staff} onClose={() => setPanel(null)}
          onAction={act} onSetStatus={setStatus} onReassign={reassign} saving={saving}
          onEdit={(a) => { setPanel(null); setEditing(a); }} />
      )}
      {editing && <EditModal appt={editing} employees={staff} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}
